import React, { useContext } from "react";
import { Box, Button, Typography } from "@mui/material";
import { Auth, API } from "aws-amplify";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import UserContext from "./UserContext";
import { useNavigate } from "react-router-dom";
import { buildApiHeaders } from "./apiHeaders";

const INK = "#0f1121";
const INK_SOFT = "rgba(15, 17, 33, 0.62)";
const INK_FAINT = "rgba(15, 17, 33, 0.42)";
const INK_HAIRLINE = "rgba(15, 17, 33, 0.08)";
const ACCENT = "#3854e8";
const SURFACE = "#ffffff";

const MONO =
  "'JetBrains Mono', 'Söhne Mono', ui-monospace, SFMono-Regular, Menlo, monospace";
const SANS = "'sohne-var', 'Helvetica Neue', 'Arial', sans-serif";

const initialFromName = name => {
  if (!name) return "·";
  const parts = String(name).trim().split(/\s+/);
  return (parts[0]?.[0] || "").toUpperCase() + (parts[1]?.[0] || "").toUpperCase();
};

const primaryButtonSx = {
  fontSize: "0.92rem",
  fontWeight: 600,
  fontFamily: SANS,
  textTransform: "none",
  letterSpacing: "0.2px",
  px: 3,
  py: 1.2,
  borderRadius: "12px",
  color: "#fafaf7",
  backgroundColor: INK,
  boxShadow:
    "inset 0 1px 0 rgba(255, 255, 255, 0.08), 0 8px 18px -10px rgba(15, 17, 33, 0.45)",
  transition:
    "transform 0.15s ease, box-shadow 0.2s ease, background-color 0.2s ease",
  "&:hover": {
    backgroundColor: "#1a1d36",
    transform: "translateY(-1px)"
  },
  "&:active": { transform: "translateY(0) scale(0.985)" }
};

const ghostButtonSx = {
  fontSize: "0.88rem",
  fontWeight: 500,
  fontFamily: SANS,
  textTransform: "none",
  letterSpacing: "0.2px",
  px: 2,
  py: 1.1,
  borderRadius: "12px",
  color: INK_SOFT,
  backgroundColor: "transparent",
  border: `1px solid ${INK_HAIRLINE}`,
  transition: "all 0.15s ease",
  "&:hover": {
    color: INK,
    borderColor: "rgba(15, 17, 33, 0.32)",
    backgroundColor: "rgba(15, 17, 33, 0.02)",
    transform: "translateY(-1px)"
  },
  "&:active": { transform: "translateY(0) scale(0.985)" }
};

export default function ProfileCard({
  name,
  subscriptionType,
  email,
  subscriptionID
}) {
  const navigate = useNavigate();
  const { onSuccess, setUser, setSubscriptionType } = useContext(UserContext);

  const handleCancelSubscription = async () => {
    try {
      const headers = await buildApiHeaders();
      // Backend looks the subscription up by Cognito sub — body is empty
      // by design so a caller can't cancel someone else's subscription.
      await API.post("chatEndpoints", "/chat/cancel_subscription", {
        body: {},
        headers
      });
      // Optimistic local update; the webhook is the authoritative writer
      // of subscriptionType in DynamoDB.
      setSubscriptionType("FREE");
    } catch (error) {
      // Leave the UI as-is; the user can retry.
    }
  };

  const handleUpgradeClick = e => {
    e.preventDefault();
    navigate("/upgrade", { state: { onSuccess, name, email } });
  };

  const handleSignOut = () => {
    try {
      Auth.signOut().then(() => {
        setUser(null);
        navigate("/");
      });
    } catch (error) {
      console.log(error);
    }
  };

  const isPremium = subscriptionType === "PREMIUM";
  const planLabel = subscriptionType
    ? subscriptionType[0] + subscriptionType.slice(1).toLowerCase()
    : "—";

  return (
    <Box
      sx={{
        backgroundColor: SURFACE,
        borderRadius: "24px",
        border: `1px solid ${INK_HAIRLINE}`,
        boxShadow:
          "inset 0 1px 0 rgba(255, 255, 255, 0.6), 0 24px 60px -32px rgba(15, 17, 33, 0.18), 0 2px 6px -2px rgba(15, 17, 33, 0.05)",
        p: { xs: 3, sm: 4 },
        textAlign: "left"
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: "18px",
            backgroundColor: INK,
            color: "#fafaf7",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontFamily: MONO,
            fontSize: "1.05rem",
            fontWeight: 600,
            letterSpacing: "0.04em",
            boxShadow:
              "inset 0 1px 0 rgba(255, 255, 255, 0.12), 0 6px 14px -8px rgba(15, 17, 33, 0.55)"
          }}
        >
          {initialFromName(name)}
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            sx={{
              fontFamily: SANS,
              fontSize: "1.18rem",
              fontWeight: 600,
              lineHeight: 1.2,
              color: INK,
              letterSpacing: "-0.01em"
            }}
          >
            {name || "Welcome"}
          </Typography>
          <Typography
            sx={{
              fontFamily: SANS,
              fontSize: "0.88rem",
              color: INK_SOFT,
              lineHeight: 1.3,
              mt: 0.25,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis"
            }}
            title={email}
          >
            {email || "—"}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1.5,
          py: 1.75,
          borderTop: `1px solid ${INK_HAIRLINE}`,
          borderBottom: `1px solid ${INK_HAIRLINE}`,
          mb: 3
        }}
      >
        <Typography
          sx={{
            fontFamily: MONO,
            fontSize: "0.7rem",
            fontWeight: 500,
            letterSpacing: "0.16em",
            textTransform: "uppercase",
            color: INK_FAINT
          }}
        >
          Plan
        </Typography>
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.75,
            px: 1.25,
            py: 0.4,
            borderRadius: "999px",
            backgroundColor: isPremium ? INK : "transparent",
            border: isPremium
              ? `1px solid ${INK}`
              : `1px solid ${INK_HAIRLINE}`,
            color: isPremium ? "#fafaf7" : INK
          }}
        >
          {isPremium && <BoltRoundedIcon sx={{ fontSize: 14 }} />}
          <Typography
            sx={{
              fontFamily: SANS,
              fontSize: "0.78rem",
              fontWeight: 600,
              letterSpacing: "0.02em"
            }}
          >
            {planLabel}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {!isPremium && (
          <Button
            onClick={handleUpgradeClick}
            variant="contained"
            startIcon={<BoltRoundedIcon sx={{ fontSize: 18 }} />}
            sx={primaryButtonSx}
          >
            Upgrade to Premium
          </Button>
        )}
        {isPremium && subscriptionID && (
          <Button
            onClick={handleCancelSubscription}
            startIcon={<CancelOutlinedIcon sx={{ fontSize: 16 }} />}
            sx={ghostButtonSx}
          >
            Cancel subscription
          </Button>
        )}
        <Button
          onClick={handleSignOut}
          startIcon={<LogoutRoundedIcon sx={{ fontSize: 16 }} />}
          sx={ghostButtonSx}
        >
          Sign out
        </Button>
      </Box>
    </Box>
  );
}
